import React from "react";
import { useOutletContext } from "react-router-dom";
import { useEffect, useState } from 'react';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';
import { supabase } from './supabaseClient';
import { updateBookingRequestStatus, respondToReschedule } from './bookingService';
import PageHeader from './components/PageHeader';
import { createMapsUrl } from './components/location/locationUtils';
import { toArabicNumerals, translateSubject, transliterateNameSync } from './utils/arabicNumbers';
import { 
  FaTimes, 
  FaHourglassHalf, 
  FaCheck, 
  FaRedo, 
  FaClipboardList, 
  FaInbox, 
  FaCalendarAlt, 
  FaUsers, 
  FaUser, 
  FaGlobe, 
  FaMapMarkerAlt, 
  FaDollarSign, 
  FaChartBar, 
  FaExclamationTriangle,
  FaSchool,
  FaBook,
  FaLock,
  FaBriefcase,
  FaLink,
  FaBolt,
  FaCheckCircle,
  FaCreditCard,
  FaTimesCircle,
  FaClock
} from 'react-icons/fa';

function StudentProfileModal({ open, onClose, student, isArabic }) {
  if (!open || !student) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={onClose}>
      <div className="bg-white rounded-xl p-8 max-w-md w-full relative mx-4" onClick={e => e.stopPropagation()}>
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
        >
          ✕
        </button>
        <div className="flex flex-col items-center">
          <img 
            src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'} 
            alt={student.full_name} 
            className="w-24 h-24 rounded-full object-cover border-3 border-gray-200 mb-4 shadow-lg" 
          />
          <h2 className="text-xl font-bold mb-2 text-gray-900">{student.full_name}</h2>
          {student.school || student.university ? (
            <div className="text-gray-600 mb-2 flex items-center gap-2">
              <FaSchool style={{ fontSize: '16px', color: '#3b82f6' }} />
              <span>{student.school || student.university}</span>
            </div>
          ) : null}
          {student.grade_level && (
            <div className="text-gray-600 mb-4 flex items-center gap-2">
              <FaBook style={{ fontSize: '16px' }} />
              <span>{student.grade_level}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestDetailsModal({ open, onClose, request, student, isArabic, onAction }) {
  if (!open || !request) return null;
  
  const copyMeetingLink = () => {
    if (request.meeting_link) {
      navigator.clipboard.writeText(request.meeting_link);
      alert(isArabic ? 'تم نسخ الرابط' : 'Meeting link copied!');
    }
  };

  const getStatusConfig = (status, paymentStatus) => {
    // If accepted but payment pending, show as "Awaiting Payment"
    if (status === 'accepted' && paymentStatus === 'awaiting_payment') {
      return {
        color: '#f59e0b',
        bg: '#fffbeb',
        icon: <FaCreditCard size={14} />,
        label: isArabic ? 'في انتظار الدفع' : 'Awaiting Payment'
      };
    }

    // If accepted and paid, show as "Confirmed"
    if (status === 'accepted' && paymentStatus === 'paid') {
      return {
        color: '#16a34a',
        bg: '#f0fdf4',
        icon: <FaCheckCircle size={14} />,
        label: isArabic ? 'مؤكد' : 'Confirmed'
      };
    }

    // Default statuses
    const configs = {
      pending: { color: '#d97706', bg: '#fff7ed', icon: <FaHourglassHalf size={14} />, label: isArabic ? 'قيد الانتظار' : 'Pending' },
      declined: { color: '#dc2626', bg: '#fef2f2', icon: <FaTimesCircle size={14} />, label: isArabic ? 'مرفوض' : 'Declined' },
      rejected: { color: '#dc2626', bg: '#fef2f2', icon: <FaTimesCircle size={14} />, label: isArabic ? 'مرفوض' : 'Rejected' }
    };
    return configs[status] || configs.pending;
  };

  const statusConfig = getStatusConfig(request.status, request.payment_status);

  // Calculate duration
  const parseTime = (timeStr) => {
    const match = timeStr?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return { hours, minutes };
  };

  const startTime = parseTime(request.time_from);
  const endTime = parseTime(request.time_to);
  let duration = null;
  if (startTime && endTime) {
    const startMinutes = startTime.hours * 60 + startTime.minutes;
    const endMinutes = endTime.hours * 60 + endTime.minutes;
    const diffMinutes = endMinutes - startMinutes;
    duration = diffMinutes > 0 ? `${diffMinutes} ${isArabic ? 'دقيقة' : 'minutes'}` : null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl p-6 max-w-2xl w-full relative max-h-[90vh] overflow-y-auto" 
        onClick={e => e.stopPropagation()}
        dir={isArabic ? 'rtl' : 'ltr'}
      >
        <button
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
        >
          <FaTimes />
        </button>
        
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900">{isArabic ? 'تفاصيل الطلب' : 'Request Details'}</h2>
            <div 
              className="px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2"
              style={{ background: statusConfig.bg, color: statusConfig.color }}
            >
              {statusConfig.icon}
              <span>{statusConfig.label}</span>
            </div>
          </div>
        </div>

        {/* Student Info */}
        {student && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-4">
              <img 
                src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'} 
                alt={student.full_name} 
                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md" 
              />
              <div>
                <div className="font-bold text-lg text-gray-900">{student.full_name}</div>
                {student.school || student.university ? (
                  <div className="text-sm text-gray-600">{student.school || student.university}</div>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Booking Details */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <FaCalendarAlt style={{ fontSize: '14px', color: '#3b82f6' }} />
                <span>{isArabic ? 'التاريخ' : 'Date'}</span>
              </div>
              <div className="font-bold text-gray-900">
                {request.date ? (() => {
                  try {
                    const date = request.date.includes('T') ? parseISO(request.date) : new Date(request.date + 'T00:00:00');
                    return isNaN(date.getTime()) ? request.date : format(date, 'EEEE, MMMM d, yyyy');
                  } catch {
                    return request.date;
                  }
                })() : request.date}
              </div>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <FaCalendarAlt style={{ fontSize: '14px', color: '#3b82f6' }} />
                <span>{isArabic ? 'الوقت' : 'Time'}</span>
              </div>
              <div className="font-bold text-gray-900">
                {request.time_from} - {request.time_to}
                {duration && <span className="text-sm font-normal text-gray-600 ml-2">({duration})</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <FaBook style={{ fontSize: '14px', color: '#8b5cf6' }} />
                <span>{isArabic ? 'المادة' : 'Subject'}</span>
              </div>
              <div className="font-bold text-gray-900">{request.subject || (isArabic ? 'غير محدد' : 'Not specified')}</div>
            </div>

            <div className="p-4 bg-orange-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                {request.request_type === 'group' ? <FaUsers style={{ fontSize: '14px', color: '#6366f1' }} /> : <FaUser style={{ fontSize: '14px', color: '#6366f1' }} />}
                <span>{isArabic ? 'نوع الجلسة' : 'Session Type'}</span>
              </div>
              <div className="font-bold text-gray-900 capitalize">
                {request.request_type === 'group' ? (isArabic ? 'جماعية' : 'Group') : (isArabic ? 'فردية' : 'Individual')}
                {request.num_students && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    ({request.num_students} {isArabic ? 'طلاب' : 'students'})
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Session Format & Location */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
              {request.lesson_format === 'online' || request.place === 'online' ? (
                <>
                  <FaGlobe style={{ fontSize: '14px', color: '#2563eb' }} />
                  <span>{isArabic ? 'نوع الجلسة' : 'Session Type'}</span>
                </>
              ) : (
                <>
                  <FaMapMarkerAlt style={{ fontSize: '14px', color: '#ef4444' }} />
                  <span>{isArabic ? 'الموقع' : 'Location'}</span>
                </>
              )}
            </div>
            <div className="font-bold text-gray-900">
              {request.lesson_format === 'online' || request.place === 'online' ? (
                <span style={{ color: '#2563eb' }}>{isArabic ? 'جلسة عبر الإنترنت' : 'Online Session'}</span>
              ) : request.location_latitude && request.location_longitude ? (
                <a
                  href={createMapsUrl(request.location_latitude, request.location_longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                >
                  {isArabic 
                    ? toArabicNumerals(request.location_formatted_address || request.place || 'عرض الموقع', isArabic)
                    : (request.location_formatted_address || request.place || 'View Location')}
                  <span style={{ fontSize: '12px' }}>↗</span>
                </a>
              ) : (
                <span>{isArabic 
                  ? toArabicNumerals(request.place || 'سيتم الترتيب', isArabic)
                  : (request.place || 'To be arranged')}</span>
              )}
            </div>
          </div>

          {request.total_price && (
            <div className="p-4 bg-yellow-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <FaDollarSign style={{ fontSize: '14px', color: '#10b981' }} />
                <span>{isArabic ? 'السعر الكلي' : 'Total Price'}</span>
              </div>
              <div className="font-bold text-2xl text-gray-900">{request.total_price} BD</div>
            </div>
          )}

          {request.meeting_link && request.payment_status === 'paid' && (request.lesson_format === 'online' || request.place === 'online') && (
            <div className="p-4 bg-indigo-50 rounded-xl">
              <div className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                <FaLink style={{ fontSize: '14px', color: '#2563eb' }} />
                <span>{isArabic ? 'رابط الاجتماع' : 'Meeting Link'}</span>
              </div>
              <div className="flex items-center gap-2">
                <a 
                  href={request.meeting_link} 
                  target="_blank" 
                  rel="noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-semibold text-sm break-all flex-1"
                >
                  {request.meeting_link}
                </a>
                <button
                  onClick={copyMeetingLink}
                  className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition whitespace-nowrap"
                >
                  {isArabic ? 'نسخ' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        {request.status === 'pending' && onAction && (
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                onAction(request.id, 'accepted');
                onClose();
              }}
              className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition flex items-center justify-center gap-2"
            >
              <FaCheck style={{ color: '#ffffff' }} /> {isArabic ? 'اقبل الطلب' : 'Accept Request'}
            </button>
            <button
              onClick={() => {
                onAction(request.id, 'declined');
                onClose();
              }}
              className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition flex items-center justify-center gap-2"
            >
              <FaTimes style={{ color: '#6b7280' }} /> {isArabic ? 'رفض الطلب' : 'Reject Request'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RequestsPage() {
  const { isArabic, sidebarVisible } = useOutletContext?.() || {};
  const [teacherEmail, setTeacherEmail] = useState('');
  const [allRequests, setAllRequests] = useState([]);
  const [rescheduleRequests, setRescheduleRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'accepted', 'declined', 'all', 'reschedule'
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [studentProfiles, setStudentProfiles] = useState({});
  const [profileModal, setProfileModal] = useState({ open: false, student: null });
  const [requestModal, setRequestModal] = useState({ open: false, request: null });
  const [loading, setLoading] = useState(true);
  const [showBenefitsBanner, setShowBenefitsBanner] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Get user email from Supabase session instead of localStorage
  useEffect(() => {
    const getSessionEmail = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setTeacherEmail(session.user.email);
      }
    };
    getSessionEmail();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter requests based on active tab
  const filteredRequests = React.useMemo(() => {
    if (activeTab === 'all') return allRequests;
    if (activeTab === 'pending') return allRequests.filter(r => r.status === 'pending');
    if (activeTab === 'accepted') return allRequests.filter(r => r.status === 'accepted');
    if (activeTab === 'declined') return allRequests.filter(r => r.status === 'declined' || r.status === 'rejected');
    return [];
  }, [allRequests, activeTab]);

  // Fetch and subscribe to requests
  useEffect(() => {
    if (!teacherEmail) return;
    let mounted = true;
    async function fetchRequests() {
      setLoading(true);
      const { data } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .order('created_at', { ascending: false });
      if (mounted) {
        setAllRequests(data || []);
        setLoading(false);
      }
      
      const { data: rescheduleData } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .eq('reschedule_status', 'pending')
        .order('reschedule_requested_at', { ascending: false });
      if (mounted) setRescheduleRequests(rescheduleData || []);
    }
    fetchRequests();
    const channel = supabase
      .channel('teacher-requests')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'booking_requests', filter: `teacher_email=eq.${teacherEmail}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setAllRequests(prev => [payload.new, ...prev]);
            if (payload.new.status === 'pending') {
              // Already added above
            }
          } else if (payload.eventType === 'UPDATE') {
            setAllRequests(prev => prev.map((req) => (req.id === payload.new.id ? payload.new : req)));

            if (payload.new.reschedule_status === 'pending') {
              setRescheduleRequests(prev => {
                const exists = prev.some(r => r.id === payload.new.id);
                if (exists) {
                  return prev.map(r => (r.id === payload.new.id ? payload.new : r));
                }
                return [payload.new, ...prev];
              });
            } else if (payload.old?.reschedule_status === 'pending' && payload.new.reschedule_status !== 'pending') {
              setRescheduleRequests(prev => prev.filter(r => r.id !== payload.new.id));
            }
          } else if (payload.eventType === 'DELETE') {
            setAllRequests(prev => prev.filter((req) => req.id !== payload.old.id));
            setRescheduleRequests(prev => prev.filter((req) => req.id !== payload.old.id));
          }
        }
      )
      .subscribe();
    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [teacherEmail]);

  // Fetch student profiles for all requests
  useEffect(() => {
    async function fetchProfiles() {
      const emails = Array.from(new Set([...allRequests, ...rescheduleRequests].map(r => r.student_email)));
      if (emails.length === 0) return;
      const { data } = await supabase.from('students').select('*').in('email', emails);
      const profiles = {};
      (data || []).forEach(s => { profiles[s.email] = s; });
      setStudentProfiles(profiles);
    }
    fetchProfiles();
  }, [allRequests, rescheduleRequests]);

  async function handleAction(reqId, status) {
    setActionLoading(reqId + '-' + status);
    setActionError('');
    setActionSuccess('');
    try {
      const { data, error } = await updateBookingRequestStatus(reqId, status);
      if (error) throw error;

      // Refresh the data after successful status update
      const { data: refreshedData } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .order('created_at', { ascending: false });

      if (refreshedData) {
        setAllRequests(refreshedData);
      }

      setActionSuccess(isArabic ? `تم ${status === 'accepted' ? 'قبول' : 'رفض'} الطلب بنجاح!` : `Request ${status}ed successfully!`);
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      setActionError(isArabic ? `فشل تحديث الطلب: ${err.message || err}` : `Failed to update request: ${err.message || err}`);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRescheduleAction(reqId, decision) {
    setActionLoading(reqId + '-reschedule-' + decision);
    setActionError('');
    setActionSuccess('');
    try {
      await respondToReschedule(reqId, { decision, teacherId: null });

      // Refresh reschedule requests after successful action
      const { data: refreshedRescheduleData } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .eq('reschedule_status', 'pending')
        .order('reschedule_requested_at', { ascending: false });

      if (refreshedRescheduleData) {
        setRescheduleRequests(refreshedRescheduleData);
      }

      // Also refresh all requests to update the main list
      const { data: refreshedAllData } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('teacher_email', teacherEmail)
        .order('created_at', { ascending: false });

      if (refreshedAllData) {
        setAllRequests(refreshedAllData);
      }

      setActionSuccess(decision === 'accepted'
        ? (isArabic ? 'تم قبول إعادة الجدولة!' : 'Reschedule accepted!')
        : (isArabic ? 'تم رفض إعادة الجدولة' : 'Reschedule declined.'));
      setTimeout(() => setActionSuccess(''), 3000);
    } catch (err) {
      setActionError(isArabic ? `فشل تحديث إعادة الجدولة: ${err.message || err}` : `Failed to update reschedule: ${err.message || err}`);
    } finally {
      setActionLoading(null);
    }
  }

  const copyMeetingLink = (link) => {
    navigator.clipboard.writeText(link);
    setActionSuccess(isArabic ? 'تم نسخ الرابط' : 'Meeting link copied!');
    setTimeout(() => setActionSuccess(''), 2000);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Handle YYYY-MM-DD format
      const date = dateStr.includes('T') ? parseISO(dateStr) : new Date(dateStr + 'T00:00:00');
      if (isNaN(date.getTime())) return dateStr;
      if (isToday(date)) return isArabic ? 'اليوم' : 'Today';
      if (isTomorrow(date)) return isArabic ? 'غداً' : 'Tomorrow';
      return format(date, isArabic ? 'd MMM yyyy' : 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getStatusConfig = (status, paymentStatus) => {
    // If accepted but payment pending, show as "Awaiting Payment"
    if (status === 'accepted' && paymentStatus === 'awaiting_payment') {
      return {
        color: '#f59e0b',
        bg: '#fffbeb',
        icon: <FaCreditCard size={14} />,
        label: isArabic ? 'في انتظار الدفع' : 'Awaiting Payment'
      };
    }

    // If accepted and paid, show as "Confirmed"
    if (status === 'accepted' && paymentStatus === 'paid') {
      return {
        color: '#16a34a',
        bg: '#f0fdf4',
        icon: <FaCheckCircle size={14} />,
        label: isArabic ? 'مؤكد' : 'Confirmed'
      };
    }

    // Default statuses
    const configs = {
      pending: { color: '#d97706', bg: '#fff7ed', icon: <FaHourglassHalf size={14} />, label: isArabic ? 'قيد الانتظار' : 'Pending' },
      declined: { color: '#dc2626', bg: '#fef2f2', icon: <FaTimesCircle size={14} />, label: isArabic ? 'مرفوض' : 'Declined' },
      rejected: { color: '#dc2626', bg: '#fef2f2', icon: <FaTimesCircle size={14} />, label: isArabic ? 'مرفوض' : 'Rejected' }
    };
    return configs[status] || configs.pending;
  };

  const tabs = [
    { id: 'pending', label: isArabic ? 'قيد الانتظار' : 'Pending', icon: <FaHourglassHalf style={{ color: '#d97706' }} />, count: allRequests.filter(r => r.status === 'pending').length },
    { id: 'accepted', label: isArabic ? 'مقبول' : 'Accepted', icon: <FaCheck style={{ color: '#16a34a' }} />, count: allRequests.filter(r => r.status === 'accepted').length },
    { id: 'declined', label: isArabic ? 'مرفوض' : 'Declined', icon: <FaTimes style={{ color: '#dc2626' }} />, count: allRequests.filter(r => r.status === 'declined' || r.status === 'rejected').length },
    { id: 'reschedule', label: isArabic ? 'إعادة الجدولة' : 'Reschedule', icon: <FaRedo style={{ color: '#E86A0A' }} />, count: rescheduleRequests.length },
    { id: 'all', label: isArabic ? 'الكل' : 'All', icon: <FaClipboardList style={{ color: '#6b7280' }} />, count: allRequests.length }
  ];

  return (
    <main
      style={{
        flex: 1,
        padding: isMobile ? '16px' : '40px 48px',
        paddingLeft: isMobile ? '16px' : (sidebarVisible ? '72px' : '48px'),
        minHeight: '100vh',
        background: '#f7f9fb',
        fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
        transition: 'padding-left 0.3s',
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <PageHeader 
        title={isArabic ? 'جميع الطلبات' : 'All Requests'} 
        subtitle={isArabic ? 'إدارة طلبات الحجز من الطلاب' : 'Manage booking requests from students'} 
        isArabic={isArabic} 
      />

      {/* Alert Messages */}
      {actionError && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '14px 20px',
          borderRadius: 12,
          marginBottom: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid #fca5a5'
        }}>
          <FaExclamationTriangle style={{ fontSize: '18px', flexShrink: 0, color: '#f59e0b' }} />
          <span>{actionError}</span>
        </div>
      )}
      {actionSuccess && (
        <div style={{
          background: '#dcfce7',
          color: '#166534',
          padding: '14px 20px',
          borderRadius: 12,
          marginBottom: 20,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: '1px solid #86efac'
        }}>
          <FaCheck style={{ fontSize: '18px', flexShrink: 0, color: '#16a34a' }} />
          <span>{actionSuccess}</span>
        </div>
      )}

      {/* Platform Benefits Banner */}
      {showBenefitsBanner && (
      <div style={{
        background: 'linear-gradient(135deg, #E86A0A 0%, #CC5500 100%)',
        borderRadius: isMobile ? 12 : 16,
        padding: isMobile ? '14px' : 20,
        paddingRight: isMobile ? '40px' : 20,
        marginBottom: isMobile ? 16 : 24,
        color: '#fff',
        boxShadow: '0 4px 16px rgba(232,106,10,0.3)',
        position: 'relative'
      }}>
          {/* Close Button */}
          <button
            onClick={() => setShowBenefitsBanner(false)}
            style={{
              position: 'absolute',
              top: isMobile ? 10 : 12,
              right: isArabic ? 'auto' : (isMobile ? 10 : 12),
              left: isArabic ? (isMobile ? 10 : 12) : 'auto',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: isMobile ? 26 : 28,
              height: isMobile ? 26 : 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#fff',
              fontSize: isMobile ? 14 : 16,
              fontWeight: 700,
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            aria-label="Close"
          >
            <FaTimes style={{ color: '#ffffff' }} />
          </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, marginBottom: isMobile ? 12 : 16 }}>
          <FaBriefcase style={{ fontSize: isMobile ? 20 : 24, color: '#ffffff' }} />
          <h3 style={{ margin: 0, fontSize: isMobile ? 14 : 18, fontWeight: 700 }}>
            {isArabic ? 'مزايا الحجز عبر المنصة' : 'Platform Booking Benefits'}
          </h3>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          gap: isMobile ? 10 : 16,
          fontSize: isMobile ? 11 : 13
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaDollarSign style={{ fontSize: '16px', color: '#10b981' }} />
            <span>{isArabic ? 'ضمان الدفع السريع (24-48 ساعة)' : 'Fast Payment Guarantee (24-48 hours)'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaLock style={{ fontSize: '16px', color: '#ffffff' }} />
            <span>{isArabic ? 'حماية من عدم الدفع' : 'Protection from non-payment'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaChartBar style={{ fontSize: '16px', color: '#ffffff' }} />
            <span>{isArabic ? 'تتبع الأرباح والتحليلات' : 'Earnings & Analytics Tracking'}</span>
          </div>
        </div>
        <div style={{
          marginTop: isMobile ? 10 : 12,
          padding: isMobile ? 10 : 12,
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 8,
          fontSize: isMobile ? 10 : 12,
          fontWeight: 600,
          lineHeight: 1.4
        }}>
          <FaExclamationTriangle style={{ marginRight: '6px', display: 'inline-block', color: '#f59e0b' }} /> {isArabic
            ? 'الحجوزات خارج المنصة لا تشمل هذه المزايا وقد تؤدي إلى تعليق الحساب'
            : 'Off-platform bookings do not include these benefits and may result in account suspension'}
        </div>
      </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: isMobile ? 6 : 8,
        marginBottom: isMobile ? 16 : 24,
        flexWrap: 'wrap',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: isMobile ? 6 : 8,
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: isMobile ? '8px 12px' : '12px 20px',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: isMobile ? 12 : 14,
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: 'none',
              background: activeTab === tab.id ? '#E86A0A' : 'transparent',
              color: activeTab === tab.id ? '#fff' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 4 : 8,
              position: 'relative',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseOver={e => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }
            }}
            onMouseOut={e => {
              if (activeTab !== tab.id) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {activeTab === tab.id 
                ? React.cloneElement(tab.icon, { style: { ...tab.icon.props.style, color: '#ffffff' } })
                : tab.icon
              }
            </span>
            <span>{tab.label}</span>
            {tab.count > 0 && (
              <span style={{
                background: activeTab === tab.id ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
                color: activeTab === tab.id ? '#fff' : '#6b7280',
                padding: isMobile ? '1px 6px' : '2px 8px',
                borderRadius: 12,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                marginLeft: isMobile ? 2 : 4
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              height: 400,
              animation: 'pulse 1.5s ease-in-out infinite'
            }}></div>
          ))}
        </div>
      )}

      {/* Requests Grid */}
      {!loading && activeTab !== 'reschedule' && (
        <>
          {filteredRequests.length === 0 ? (
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 60,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <FaInbox style={{ fontSize: 64, color: '#E86A0A' }} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                {isArabic ? 'لا توجد طلبات' : 'No requests'}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {activeTab === 'pending' 
                  ? (isArabic ? 'لا توجد طلبات قيد الانتظار حالياً' : 'No pending requests at the moment')
                  : activeTab === 'accepted'
                  ? (isArabic ? 'لا توجد جلسات مقبولة' : 'No accepted sessions')
                  : activeTab === 'declined'
                  ? (isArabic ? 'لا توجد طلبات مرفوضة' : 'No declined requests')
                  : (isArabic ? 'لا توجد طلبات' : 'No requests found')
                }
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: isMobile ? 16 : 20
            }}>
              {filteredRequests.map((req) => {
                const student = studentProfiles[req.student_email] || {};
                const statusConfig = getStatusConfig(req.status, req.payment_status);
                const isUpcoming = req.status === 'accepted' && (() => {
                  try {
                    const dateStr = req.date + 'T' + (req.time_to || '23:59');
                    const date = dateStr.includes('T') ? parseISO(dateStr) : new Date(dateStr);
                    return !isNaN(date.getTime()) && !isPast(date);
                  } catch {
                    return false;
                  }
                })();
                
                return (
                  <div 
                    key={req.id} 
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: `2px solid ${statusConfig.bg}`,
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    {/* Status Badge */}
                    <div style={{
                      position: 'absolute',
                      top: 16,
                      [isArabic ? 'left' : 'right']: 16,
                      padding: '4px 12px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 700,
                      background: statusConfig.bg,
                      color: statusConfig.color,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <span>{statusConfig.icon}</span>
                      <span>{statusConfig.label}</span>
                    </div>

                    {/* Student Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <img 
                        src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'} 
                        alt={student.full_name || req.student_name} 
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '3px solid #e5e7eb',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#111827', marginBottom: 2 }}>
                          {isArabic 
                            ? transliterateNameSync(student.full_name || req.student_name, isArabic)
                            : (student.full_name || req.student_name)}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                          {isArabic ? 'طالب' : 'Student'}
                        </div>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div style={{ marginBottom: 16, padding: 12, background: '#f9fafb', borderRadius: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <FaCalendarAlt style={{ fontSize: '16px', color: '#3b82f6' }} />
                        <span style={{ fontWeight: 700, color: '#374151' }}>
                          {isArabic ? toArabicNumerals(formatDateDisplay(req.date), isArabic) : formatDateDisplay(req.date)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <FaCalendarAlt style={{ fontSize: '16px', color: '#3b82f6' }} />
                        <span style={{ fontWeight: 600, color: '#6b7280' }}>
                          {isArabic ? toArabicNumerals(req.time_from, isArabic) : req.time_from} - {isArabic ? toArabicNumerals(req.time_to, isArabic) : req.time_to}
                        </span>
                      </div>
                      {req.subject && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <FaBook style={{ fontSize: '16px', color: '#8b5cf6' }} />
                          <span style={{ fontWeight: 600, color: '#6b7280' }}>
                            {isArabic ? translateSubject(req.subject, isArabic) : req.subject}
                          </span>
                        </div>
                      )}
                      {/* Session Format & Location */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        {req.lesson_format === 'online' || req.place === 'online' ? (
                          <>
                            <FaGlobe style={{ fontSize: '16px', color: '#2563eb' }} />
                            <span style={{ fontWeight: 700, color: '#2563eb' }}>
                              {isArabic ? 'جلسة عبر الإنترنت' : 'Online Session'}
                            </span>
                          </>
                        ) : (
                          <>
                            <FaMapMarkerAlt style={{ fontSize: '16px', color: '#ef4444' }} />
                            <span style={{ fontWeight: 700, color: '#374151' }}>
                              {isArabic ? 'الموقع: ' : 'Location: '}
                            </span>
                            {req.location_latitude && req.location_longitude ? (
                              <a
                                href={createMapsUrl(req.location_latitude, req.location_longitude)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {isArabic 
                                  ? toArabicNumerals(req.location_formatted_address || req.place || 'عرض الموقع', isArabic)
                                  : (req.location_formatted_address || req.place || 'View Location')} ↗
                              </a>
                            ) : (
                              <span style={{ fontWeight: 600, color: '#374151' }}>
                                {isArabic 
                                  ? toArabicNumerals(req.place || 'سيتم الترتيب', isArabic)
                                  : (req.place || 'To be arranged')}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {req.request_type === 'group' ? <FaUsers style={{ fontSize: '16px', color: '#6366f1' }} /> : <FaUser style={{ fontSize: '16px', color: '#6366f1' }} />}
                        <span style={{ fontWeight: 600, color: '#6b7280' }}>
                          {isArabic 
                            ? (req.request_type === 'group' ? 'جماعية' : 'فردية')
                            : (req.request_type || 'individual')}
                          {req.num_students && ` • ${isArabic ? toArabicNumerals(req.num_students, isArabic) : req.num_students} ${isArabic ? 'طلاب' : 'students'}`}
                        </span>
                      </div>
                    </div>

                    {/* Price & Payment Status */}
                    {req.total_price && req.status === 'accepted' && (
                      <div style={{
                        padding: 12,
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: 12,
                        marginBottom: 16,
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 12, color: '#92400e', marginBottom: 4 }}>
                          {isArabic ? 'السعر الكلي' : 'Total Price'}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 900, color: '#78350f', marginBottom: 8 }}>
                          {isArabic ? toArabicNumerals(req.total_price, isArabic) : req.total_price} {isArabic ? 'د.ب' : 'BD'}
                        </div>
                        {/* Payment Status */}
                        {(() => {
                          const sessionDate = new Date(req.date + 'T' + (req.time_to || '23:59'));
                          const isPastSession = sessionDate < new Date();
                          const daysSinceSession = isPastSession ? Math.floor((new Date() - sessionDate) / (1000 * 60 * 60 * 24)) : 0;
                          
                          let paymentStatus = 'pending';
                          let statusColor = '#d97706';
                          let statusText = isArabic ? 'في الانتظار' : 'Pending';
                          let statusIcon = <FaHourglassHalf style={{ fontSize: '14px', color: '#d97706' }} />;
                          
                          if (isPastSession) {
                            if (daysSinceSession >= 2) {
                              paymentStatus = 'processing';
                              statusColor = '#2563eb';
                              statusText = isArabic ? 'قيد المعالجة' : 'Processing';
                              statusIcon = <FaBolt style={{ fontSize: '14px', color: '#f59e0b' }} />;
                            }
                            if (daysSinceSession >= 7) {
                              paymentStatus = 'paid';
                              statusColor = '#10b981';
                              statusText = isArabic ? 'تم الدفع' : 'Paid';
                              statusIcon = <FaCheck style={{ fontSize: '14px', color: '#10b981' }} />;
                            }
                          } else {
                            paymentStatus = 'scheduled';
                            statusColor = '#6b7280';
                            statusText = isArabic ? 'مجدولة' : 'Scheduled';
                            statusIcon = <FaCalendarAlt style={{ fontSize: '14px', color: '#3b82f6' }} />;
                          }
                          
                          return (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                              padding: '6px 12px',
                              background: statusColor + '15',
                              borderRadius: 8,
                              marginTop: 8
                            }}>
                              {statusIcon}
                              <span style={{ fontSize: 12, fontWeight: 700, color: statusColor }}>
                                {statusText}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {req.total_price && req.status !== 'accepted' && (
                      <div style={{
                        padding: 12,
                        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderRadius: 12,
                        marginBottom: 16,
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 12, color: '#92400e', marginBottom: 4 }}>
                          {isArabic ? 'السعر الكلي' : 'Total Price'}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 900, color: '#78350f' }}>
                          {isArabic ? toArabicNumerals(req.total_price, isArabic) : req.total_price} {isArabic ? 'د.ب' : 'BD'}
                        </div>
                      </div>
                    )}

                    {/* Request Payment Button (for past sessions) */}
                    {req.status === 'accepted' && (() => {
                      const sessionDate = new Date(req.date + 'T' + (req.time_to || '23:59'));
                      const isPastSession = sessionDate < new Date();
                      const daysSince = isPastSession ? Math.floor((new Date() - sessionDate) / (1000 * 60 * 60 * 24)) : 0;
                      const canRequestPayment = isPastSession && daysSince >= 2 && daysSince < 7;
                      
                      if (canRequestPayment) {
                        return (
                          <div style={{ marginBottom: 16 }}>
                            <button
                              onClick={() => {
                                alert(isArabic 
                                  ? 'تم إرسال طلب الدفع. سيتم معالجته خلال 24-48 ساعة.'
                                  : 'Payment request sent. It will be processed within 24-48 hours.');
                              }}
                              style={{
                                width: '100%',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 10,
                                fontWeight: 700,
                                fontSize: 14,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                              onMouseOut={e => e.currentTarget.style.transform = 'none'}
                            >
                              <FaDollarSign style={{ fontSize: '16px', color: '#10b981' }} />
                              {isArabic ? 'طلب الدفع' : 'Request Payment'}
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Meeting Link (for paid online sessions only) */}
                    {req.meeting_link && req.payment_status === 'paid' && (req.lesson_format === 'online' || req.place === 'online') && (
                      <div style={{ marginBottom: 16 }}>
                        <button
                          onClick={() => copyMeetingLink(req.meeting_link)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={e => e.currentTarget.style.background = '#1d4ed8'}
                          onMouseOut={e => e.currentTarget.style.background = '#2563eb'}
                        >
                          <FaLink style={{ fontSize: '16px' }} />
                          <span>{isArabic ? 'نسخ رابط الاجتماع' : 'Copy Meeting Link'}</span>
                        </button>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setProfileModal({ open: true, student })}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#e5e7eb'}
                        onMouseOut={e => e.currentTarget.style.background = '#f3f4f6'}
                      >
                        {isArabic ? 'الملف الشخصي' : 'Profile'}
                      </button>
                      <button
                        onClick={() => setRequestModal({ open: true, request: req, student: student })}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = '#e5e7eb'}
                        onMouseOut={e => e.currentTarget.style.background = '#f3f4f6'}
                      >
                        {isArabic ? 'التفاصيل' : 'Details'}
                      </button>
                    </div>

                    {/* Accept/Reject Buttons (for pending) */}
                    {req.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button
                          onClick={() => handleAction(req.id, 'accepted')}
                          disabled={actionLoading === req.id + '-accepted'}
                          style={{
                            flex: 1,
                            padding: '12px',
                            background: '#16a34a',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: actionLoading === req.id + '-accepted' ? 'not-allowed' : 'pointer',
                            opacity: actionLoading === req.id + '-accepted' ? 0.6 : 1,
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6
                          }}
                          onMouseOver={e => {
                            if (actionLoading !== req.id + '-accepted') {
                              e.currentTarget.style.background = '#15803d';
                            }
                          }}
                          onMouseOut={e => {
                            if (actionLoading !== req.id + '-accepted') {
                              e.currentTarget.style.background = '#16a34a';
                            }
                          }}
                        >
                          {actionLoading === req.id + '-accepted' ? (
                            <>
                              <span style={{
                                display: 'inline-block',
                                animation: 'spin 1s linear infinite',
                                fontSize: 16
                              }}><FaHourglassHalf style={{ color: '#d97706' }} /></span>
                              <span>{isArabic ? 'جارٍ القبول...' : 'Accepting...'}</span>
                              <style>{`
                                @keyframes spin {
                                  from { transform: rotate(0deg); }
                                  to { transform: rotate(360deg); }
                                }
                              `}</style>
                            </>
                          ) : (
                            <>
                              <FaCheck style={{ fontSize: '16px', color: '#16a34a' }} />
                              <span>{isArabic ? 'اقبل' : 'Accept'}</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleAction(req.id, 'declined')}
                          disabled={actionLoading === req.id + '-declined'}
                          style={{
                            flex: 1,
                            padding: '12px',
                            background: '#dc2626',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 10,
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: actionLoading === req.id + '-declined' ? 'not-allowed' : 'pointer',
                            opacity: actionLoading === req.id + '-declined' ? 0.6 : 1,
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6
                          }}
                          onMouseOver={e => {
                            if (actionLoading !== req.id + '-declined') {
                              e.currentTarget.style.background = '#b91c1c';
                            }
                          }}
                          onMouseOut={e => {
                            if (actionLoading !== req.id + '-declined') {
                              e.currentTarget.style.background = '#dc2626';
                            }
                          }}
                        >
                          {actionLoading === req.id + '-declined' ? (
                            <>
                              <span style={{
                                display: 'inline-block',
                                animation: 'spin 1s linear infinite',
                                fontSize: 16
                              }}><FaHourglassHalf style={{ color: '#d97706' }} /></span>
                              <span>{isArabic ? 'جارٍ الرفض...' : 'Declining...'}</span>
                            </>
                          ) : (
                            <>
                              <FaTimes style={{ fontSize: '16px', color: '#dc2626' }} />
                              <span>{isArabic ? 'رفض' : 'Decline'}</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
            </div>
          );
        })}
      </div>
          )}
        </>
      )}

      {/* Reschedule Requests */}
      {!loading && activeTab === 'reschedule' && (
        <>
          {rescheduleRequests.length === 0 ? (
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 60,
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <FaRedo style={{ fontSize: 64, color: '#E86A0A' }} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
                {isArabic ? 'لا توجد طلبات إعادة جدولة' : 'No reschedule requests'}
              </div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {isArabic ? 'لا توجد طلبات إعادة جدولة في الوقت الحالي' : 'No reschedule requests at the moment'}
              </div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: isMobile ? 16 : 20
            }}>
            {rescheduleRequests.map(req => {
              const student = studentProfiles[req.student_email] || {};
              return (
                  <div 
                    key={`reschedule-${req.id}`} 
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: '2px solid #fef3c7',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    {/* Student Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                      <img 
                        src={student.profile_photo || 'https://www.gravatar.com/avatar/?d=mp&f=y'} 
                        alt={student.full_name || req.student_name} 
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '3px solid #fef3c7',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 18, color: '#111827', marginBottom: 2 }}>
                          {student.full_name || req.student_name}
                        </div>
                        <div style={{ fontSize: 12, color: '#d97706', fontWeight: 700 }}>
                          {isArabic ? 'إعادة جدولة' : 'Reschedule Request'}
                        </div>
                      </div>
                    </div>

                    {/* Time Comparison */}
                    <div style={{ 
                      background: '#fff7ed', 
                      borderRadius: 12, 
                      padding: 16, 
                      marginBottom: 16,
                      border: '1px solid #fed7aa'
                    }}>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, color: '#92400e', fontWeight: 700, marginBottom: 4 }}>
                          <FaClock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {isArabic ? 'الوقت الحالي' : 'Current Time'}
                        </div>
                        <div style={{ fontWeight: 700, color: '#78350f' }}>
                          {formatDateDisplay(req.original_date || req.date)} • {req.original_time_from || req.time_from} - {req.original_time_to || req.time_to}
                        </div>
                      </div>
                      <div style={{ 
                        width: '100%', 
                        height: 2, 
                        background: '#fed7aa', 
                        margin: '8px 0' 
                      }}></div>
                      <div>
                        <div style={{ fontSize: 11, color: '#92400e', fontWeight: 700, marginBottom: 4 }}>
                          <FaRedo style={{ fontSize: '11px', marginLeft: '4px', display: 'inline-block', color: '#E86A0A' }} /> {isArabic ? 'الوقت المقترح' : 'Proposed Time'}
                        </div>
                        <div style={{ fontWeight: 700, color: '#78350f' }}>
                          {formatDateDisplay(req.reschedule_requested_date)} • {req.reschedule_requested_time_from} - {req.reschedule_requested_time_to}
                        </div>
                      </div>
                      {req.reschedule_reason && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #fed7aa' }}>
                          <div style={{ fontSize: 11, color: '#92400e', fontWeight: 700, marginBottom: 4 }}>
                            {isArabic ? 'السبب' : 'Reason'}
                          </div>
                          <div style={{ fontSize: 13, color: '#78350f' }}>
                            {req.reschedule_reason}
                          </div>
                  </div>
                      )}
                  </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleRescheduleAction(req.id, 'accepted')}
                      disabled={actionLoading === req.id + '-reschedule-accepted'}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: '#16a34a',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: actionLoading === req.id + '-reschedule-accepted' ? 'not-allowed' : 'pointer',
                          opacity: actionLoading === req.id + '-reschedule-accepted' ? 0.6 : 1,
                          transition: 'all 0.2s'
                        }}
                      >
                        {actionLoading === req.id + '-reschedule-accepted' ? (
                          <>
                            <span style={{
                              display: 'inline-block',
                              animation: 'spin 1s linear infinite',
                              fontSize: 16
                            }}><FaHourglassHalf /></span>
                            <span>{isArabic ? 'جارٍ القبول...' : 'Accepting...'}</span>
                          </>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaCheck style={{ fontSize: '16px' }} /> {isArabic ? 'اقبل' : 'Accept'}</span>
                        )}
                    </button>
                    <button
                      onClick={() => handleRescheduleAction(req.id, 'declined')}
                      disabled={actionLoading === req.id + '-reschedule-declined'}
                        style={{
                          flex: 1,
                          padding: '12px',
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: actionLoading === req.id + '-reschedule-declined' ? 'not-allowed' : 'pointer',
                          opacity: actionLoading === req.id + '-reschedule-declined' ? 0.6 : 1,
                          transition: 'all 0.2s'
                        }}
                      >
                        {actionLoading === req.id + '-reschedule-declined' ? (
                          <>
                            <span style={{
                              display: 'inline-block',
                              animation: 'spin 1s linear infinite',
                              fontSize: 16
                            }}><FaHourglassHalf /></span>
                            <span>{isArabic ? 'جارٍ الرفض...' : 'Declining...'}</span>
                          </>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaTimes style={{ fontSize: '16px' }} /> {isArabic ? 'رفض' : 'Decline'}</span>
                        )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </>
      )}

      {/* Modals */}
      <StudentProfileModal 
        open={profileModal.open} 
        onClose={() => setProfileModal({ open: false, student: null })} 
        student={profileModal.student}
        isArabic={isArabic}
      />
      <RequestDetailsModal 
        open={requestModal.open} 
        onClose={() => setRequestModal({ open: false, request: null })} 
        request={requestModal.request}
        student={requestModal.student}
        isArabic={isArabic}
        onAction={handleAction}
      />
    </main>
  );
}
